import {
  ArchiveIcon,
  ClockClockwiseIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  ItemStatusEnum,
  useApiMutation,
  useTableDataQuery,
} from '@/modules/common';
import {
  ChangeStatusDialog,
  PaginatedTable,
  TableItemStatus,
} from '@/modules/common/components';

import { TextStyleItem } from '../types';

interface TextStylesTableProps {
  onClick: (item: TextStyleItem) => void;
}

export function TextStylesTable({ onClick }: TextStylesTableProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTextStyle, setActiveTextStyle] = useState<TextStyleItem | null>(null);
  const isArchived = activeTextStyle?.status === ItemStatusEnum.Archived;

  const { data, isFetching, refetch, ...tableProps } =
    useTableDataQuery<TextStyleItem>({
      url: '/text-styles',
      persistState: true,
    });

  const archiveTextStyleMutation = useApiMutation<TextStyleItem>(
    '/text-styles',
    { method: 'PATCH' },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
    }
  );

  const onArchiveRestoreClick = (
    event: React.SyntheticEvent<HTMLDivElement>,
    row: TextStyleItem
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveTextStyle(row);
  };

  const toggleModal = (nextOpenState: boolean) => {
    if (!nextOpenState) {
      setActiveTextStyle(null);
    }
  };

  const toggleStatus = async () => {
    if (activeTextStyle?.id) {
      setIsUpdating(true);
      try {
        await archiveTextStyleMutation.mutateAsync({
          id: activeTextStyle.id,
          status:
            activeTextStyle.status === ItemStatusEnum.Archived
              ? ItemStatusEnum.Active
              : ItemStatusEnum.Archived,
        });
        await refetch();
        setActiveTextStyle(null);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const columns: ColumnDef<TextStyleItem>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('name')}</span>
      ),
    },
    {
      accessorKey: 'content',
      header: 'Content',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('content')}</span>
      ),
    },
    {
      accessorKey: 'status',
      size: 200,
      header: 'Status',
      cell: ({ row }) => <TableItemStatus status={row.getValue('status')} />,
    },
    {
      size: 130,
      accessorKey: 'updatedAt',
      header: 'Updated Date',
      cell: ({ row }) =>
        format(new Date(row.getValue('updatedAt')), 'yyyy-MM-dd'),
    },
    {
      id: 'actions',
      size: 40,
      enableHiding: false,
      cell: ({ row }) => {
        const isRowArchived = row.original.status === ItemStatusEnum.Archived;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                data-testid="text-styles-table-actions"
              >
                <span className="sr-only">Open menu</span>
                <DotsThreeVerticalIcon color={colors.zinc[400]} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid={isRowArchived ? 'restore' : 'archive'}
                className={cn(
                  !isRowArchived && 'focus:text-destructive text-destructive'
                )}
                onClick={(event) => onArchiveRestoreClick(event, row.original)}
              >
                {isRowArchived ? (
                  <ClockClockwiseIcon />
                ) : (
                  <ArchiveIcon color="hsl(var(--destructive))" />
                )}
                {isRowArchived ? 'Restore' : 'Archive'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <PaginatedTable<TextStyleItem>
        data={data}
        onClick={onClick}
        columns={columns}
        searchable
        searchPlaceholder="Search by name"
        emptyDescription="No text styles yet"
        isLoading={isFetching}
        {...tableProps}
      />

      <ChangeStatusDialog
        onOpenChange={toggleModal}
        isArchived={isArchived}
        isUpdating={isUpdating}
        onSubmit={toggleStatus}
        isOpen={Boolean(activeTextStyle?.id)}
        title={`${isArchived ? 'Restore' : 'Archive'} text style "${activeTextStyle?.name}"?`}
        restoreDescription="Are you sure you want to restore this text style?"
        archiveDescription="Are you sure you want to archive this text style? You can restore it later if needed."
      />
    </>
  );
}
